import { TFunction } from 'i18next'

import {
  ChecklistInput,
  ChecklistInputForm,
  ChecklistInputProvidersInfo,
  ChecklistInputSimple,
  ChecklistInputType,
  ChecklistInputTypeEnum,
  ChecklistInputVehicleForm,
  ChecklistStatus,
  ChecklistStatusEnum,
} from '@/models/features/checklist.models'

import {
  ChecklistAttachmentRow,
  ChecklistInstructionsRow,
  ChecklistSignaturesRow,
  ChecklistTaskRow,
  checklistAttachmentRowSchema,
  checklistInstructionsRowSchema,
  checklistSignaturesRowSchema,
} from './checklists-task.page.types'

export const isChecklistAttachmentRow = (row: ChecklistTaskRow): row is ChecklistAttachmentRow => {
  return checklistAttachmentRowSchema.safeParse(row).success
}

export const isChecklistInstructionsRow = (
  row: ChecklistTaskRow,
): row is ChecklistInstructionsRow => {
  return checklistInstructionsRowSchema.safeParse(row).success
}

export const isChecklistSignaturesRow = (row: ChecklistTaskRow): row is ChecklistSignaturesRow => {
  return checklistSignaturesRowSchema.safeParse(row).success
}

export const isChecklistInputTypeSimple = (
  input: ChecklistInput,
): input is ChecklistInputSimple => {
  return (
    input.inputType !== ChecklistInputTypeEnum.enum.form &&
    input.inputType !== ChecklistInputTypeEnum.enum.providers_info &&
    input.inputType !== ChecklistInputTypeEnum.enum.service
  )
}

export const isChecklistInputTypeForm = (input: ChecklistInput): input is ChecklistInputForm => {
  return input.inputType === ChecklistInputTypeEnum.enum.form
}

export const isChecklistInputTypeProvidersInfo = (
  input: ChecklistInput,
): input is ChecklistInputProvidersInfo => {
  return input.inputType === ChecklistInputTypeEnum.enum.providers_info
}

export const isChecklistInputTypeVehicleForm = (
  input: ChecklistInput,
): input is ChecklistInputVehicleForm => {
  return input.inputType === ChecklistInputTypeEnum.enum.vehicle_form
}

export const isChecklistInputTypeFileUpload = (input: ChecklistInput): boolean => {
  return input.inputType === ChecklistInputTypeEnum.enum.file_upload
}

export const isChecklistInputTypeTextInfo = (input: ChecklistInput): boolean => {
  return input.inputType === ChecklistInputTypeEnum.enum.text_info
}

// Note: Show text_info and providers_info at the top of the list
export const getSortedChecklistInputs = (inputs: ChecklistInput[]): ChecklistInput[] => {
  const getRank = (input: ChecklistInput) => {
    if (isChecklistInputTypeTextInfo(input)) return 0
    if (isChecklistInputTypeProvidersInfo(input)) return 1
    return 2
  }

  return [...inputs].sort((prev, next) => {
    const rankDiff = getRank(prev) - getRank(next)

    if (rankDiff !== 0) {
      return rankDiff
    }

    return prev.position - next.position
  })
}

export const formatChecklistInputType = (
  inputType: ChecklistInputType,
  t: TFunction<['checklists']>,
): string => {
  const labels: Record<ChecklistInputType, string> = {
    [ChecklistInputTypeEnum.enum.text_info]: t('checklists:inputTypes.textInfo'),
    [ChecklistInputTypeEnum.enum.long_answer]: t('checklists:inputTypes.longAnswer'),
    [ChecklistInputTypeEnum.enum.short_answer]: t('checklists:inputTypes.shortAnswer'),
    [ChecklistInputTypeEnum.enum.providers_info]: t('checklists:inputTypes.providersInfo'),
    [ChecklistInputTypeEnum.enum.single_choice]: t('checklists:inputTypes.singleChoice'),
    [ChecklistInputTypeEnum.enum.multiple_choice]: t('checklists:inputTypes.multipleChoice'),
    [ChecklistInputTypeEnum.enum.file_upload]: t('checklists:inputTypes.fileUpload'),
    [ChecklistInputTypeEnum.enum.form]: t('checklists:inputTypes.form'),
    [ChecklistInputTypeEnum.enum.service]: t('checklists:inputTypes.service'),
    [ChecklistInputTypeEnum.enum.vehicle_form]: t('checklists:inputTypes.vehicleForm'),
  }
  return labels[inputType]
}

export const getNestedFormCount = (
  input: ChecklistInputForm | ChecklistInputVehicleForm,
): number => {
  return input.formItems?.[0]?.answers?.length ?? 0
}

export const getChecklistTaskEmptyStateDescription = (
  status: ChecklistStatus,
  t: TFunction<['checklists']>,
): string => {
  if (status === ChecklistStatusEnum.enum['not-started']) {
    return t('checklists:task.emptyState.notStarted')
  }

  if (status === ChecklistStatusEnum.enum['past-due']) {
    return t('checklists:task.emptyState.pastDue')
  }

  return t('checklists:task.emptyState.default')
}
